import React, { useState } from 'react';
import { Card, Input, Button, Alert, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { getLeadById } from '../services/apiService';

const { Title, Text } = Typography;

// Simple component to show lead details by ID
export default function SimpleLeadDetails({ defaultCompanyId = '', defaultLeadId = '' }) {
  const [leadId, setLeadId] = useState(defaultLeadId);
  const [companyId, setCompanyId] = useState(defaultCompanyId);
  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLeadDetails = async () => {
    if (!leadId.trim() || !companyId.trim()) {
      setError('Please enter both Company ID and Lead ID');
      return;
    }

    setLoading(true);
    setError(null);
    setLeadData(null);

    try {
      console.log('Fetching lead:', { companyId: companyId.trim(), leadId: leadId.trim() });
      
      // Call the getLeadById API
      const data = await getLeadById(companyId.trim(), leadId.trim());
      
      console.log('Lead data received:', data);
      setLeadData(data);
      
    } catch (err) {
      console.error('Error fetching lead:', err);
      setError(err.message || 'Failed to fetch lead details');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get project name
  const getProjectName = (lead) => {
    return lead.project_name || 
           lead.project?.name || 
           lead.lead_fields?.project_name || 
           lead.lead_fields?.project || 
           lead.campaign_name ||
           'N/A';
  };

  // Helper to get assigned user
  const getAssignedUser = (lead) => {
    if (lead.assigned_to?.name) return lead.assigned_to.name;
    if (lead.user_assigned?.name) return lead.user_assigned.name;
    
    const customFields = lead.lead_fields || {};
    const manager = customFields.sourcing_manager_5fd || 
                   customFields.closing_manager_cb3 || 
                   customFields.platform__copy__797;
    
    if (manager) {
      return Array.isArray(manager) ? manager[0] : manager;
    }
    
    return 'Not Assigned';
  };

  return (
    <Card title="Lead Details by ID" style={{ margin: '20px 0' }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Input Section */}
        <Space wrap>
          <Input
            placeholder="Company ID"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            style={{ width: 150 }}
          />
          <Input
            placeholder="Lead ID"
            value={leadId}
            onChange={(e) => setLeadId(e.target.value)}
            onPressEnter={fetchLeadDetails}
            style={{ width: 150 }}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={fetchLeadDetails}
            loading={loading}
          >
            Get Lead
          </Button>
        </Space>

        {/* Error Display */}
        {error && (
          <Alert message="Error" description={error} type="error" showIcon />
        )}

        {/* Lead Data Display */}
        {leadData && (
          <Card type="inner" title={`Lead: ${leadData.name || leadData.lead_id}`}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div><Text strong>Lead ID:</Text> {leadData.lead_id}</div>
              <div><Text strong>Name:</Text> {leadData.name || 'N/A'}</div>
              <div><Text strong>Email:</Text> {leadData.email || 'N/A'}</div>
              <div><Text strong>Phone:</Text> {leadData.phones?.[0]?.phone || leadData.phone || 'N/A'}</div>
              <div><Text strong>Project:</Text> {getProjectName(leadData)}</div>
              <div><Text strong>Assigned To:</Text> {getAssignedUser(leadData)}</div>
              <div><Text strong>Status:</Text> {leadData.status || leadData.lead_status || 'N/A'}</div>
              <div><Text strong>Source:</Text> {leadData.source || leadData.lead_source || 'N/A'}</div>
              <div><Text strong>Created:</Text> {leadData.created_at ? new Date(leadData.created_at).toLocaleDateString() : 'N/A'}</div>
              <div><Text strong>Updated:</Text> {leadData.updated_at ? new Date(leadData.updated_at).toLocaleDateString() : 'N/A'}</div>
            </div>

            {/* Raw Data for Debugging */}
            <details style={{ marginTop: '20px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                Show Raw Data (for debugging)
              </summary>
              <pre style={{ 
                backgroundColor: '#f5f5f5', 
                padding: '10px', 
                borderRadius: '4px',
                fontSize: '12px',
                overflow: 'auto',
                maxHeight: '300px',
                marginTop: '10px'
              }}>
                {JSON.stringify(leadData, null, 2)}
              </pre>
            </details>
          </Card>
        )}

        {/* Instructions */}
        {!leadData && !loading && !error && (
          <Alert
            message="Instructions"
            description="Enter a Company ID and Lead ID, then click 'Get Lead' to fetch lead details using the getLeadById API function."
            type="info"
            showIcon
          />
        )}
      </Space>
    </Card>
  );
}

// Example usage:
// <SimpleLeadDetails defaultCompanyId="your-company-id" defaultLeadId="your-lead-id" />